import type { BrokerCapabilities } from "../services/brokerIntegrationApi";

/** Matches server default `CTRADER_SYNC_MAX_LOOKBACK_DAYS` when capabilities omit a value. */
export const CTRADER_DEFAULT_OPENAPI_LOOKBACK_DAYS = 720;

export type CtraderHistoryPath = "recent_only" | "needs_csv_for_history";

export interface CtraderOpenApiSyncInfo {
  supported: boolean;
  strategy: string;
  oauthAuthorizePath: string;
  oauthCallbackPath: string;
  syncEndpoint: string;
  oauthScope: string;
  accountIdField: string;
  summary: string;
  notes: string;
  fullHistoryRecommendation: string | null;
  historyBeforeSyncRequiresCsvImport: boolean;
  csvImportEndpoint: string;
}

export const CTRADER_OAUTH_PENDING_ASSET_CLASSES_KEY = "tessera.ctrader.oauth.pending_asset_classes";
export const CTRADER_OAUTH_PENDING_ENVIRONMENT_KEY = "tessera.ctrader.oauth.pending_environment";
export const CTRADER_OAUTH_PENDING_MD_FETCH_CONSENT_KEY = "tessera.ctrader.oauth.pending_md_fetch_consent";

/**
 * OAuth redirect path only — must match server `CTRADER_OAUTH_REDIRECT_URI` and Spotware portal.
 * Do not add `?tab=…` or other query params to this URL.
 */
export const CTRADER_OAUTH_CALLBACK_PATH = "/profile/brokers/ctrader/callback";

/** Profile broker tab after OAuth (separate from redirect URI above). */
export const CTRADER_OAUTH_PROFILE_SUCCESS =
  "/profile?tab=broker-api&broker_name=ctrader&ctrader_connected=1";

export const CTRADER_OAUTH_PROFILE_BROKER_TAB = "/profile?tab=broker-api&broker_name=ctrader";

const HISTORY_PATH_STORAGE_KEY = "tessera.ctrader_trade_history_path";

function normalizeApiPath(path: string): string {
  const p = path.trim();
  if (p.startsWith("/api/")) return p.slice(4);
  return p.startsWith("/") ? p : `/${p}`;
}

function parseOpenApiSyncBlock(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o.supported === false) return null;
  return o;
}

function pickOAuthPath(
  ...candidates: Array<string | undefined | null>
): string {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return normalizeApiPath(c);
  }
  return "";
}

/** Open API sync contract from GET /broker/capabilities (ctrader). */
export function resolveCtraderOpenApiSync(
  cap: BrokerCapabilities | null | undefined
): CtraderOpenApiSyncInfo | null {
  if (!cap || String(cap.broker_name || "").toLowerCase() !== "ctrader") return null;
  const tradeSync = (cap.trade_sync ?? {}) as Record<string, unknown>;
  const openapi = parseOpenApiSyncBlock(tradeSync.openapi_sync);
  if (!openapi && !cap.auth?.supports_auto_sync) return null;

  const oauthAuthorizePath = pickOAuthPath(
    cap.auth?.oauth_authorize_url as string | undefined,
    openapi?.oauth_authorize_url as string | undefined,
    "/broker/ctrader/oauth/authorize-url"
  );
  const oauthCallbackPath = pickOAuthPath(
    cap.auth?.oauth_callback as string | undefined,
    openapi?.oauth_callback as string | undefined,
    "/broker/ctrader/oauth/callback"
  );

  const syncEndpoint = pickOAuthPath(
    openapi?.sync_endpoint as string | undefined,
    tradeSync.sync_endpoint as string | undefined,
    "/broker/trades?save_to_db=true&broker_name=ctrader"
  );

  const guidance =
    tradeSync.user_guidance && typeof tradeSync.user_guidance === "object"
      ? (tradeSync.user_guidance as Record<string, unknown>)
      : {};
  const summary =
    typeof guidance.summary === "string" && guidance.summary.trim()
      ? guidance.summary.trim()
      : "Connect your cTrader account with OAuth to sync trades automatically. Use CSV import for older statement history.";

  const csvImport = cap.csv_import ?? (tradeSync.csv_import as typeof cap.csv_import);
  const csvEndpoint =
    typeof csvImport?.endpoint === "string" && csvImport.endpoint.trim()
      ? normalizeApiPath(csvImport.endpoint)
      : "/broker/ctrader/csv-import";

  const fullHistoryRecommendation =
    tradeSync.full_history_recommendation != null
      ? String(tradeSync.full_history_recommendation).trim() || null
      : null;

  return {
    supported: Boolean(openapi?.supported ?? cap.auth?.supports_auto_sync),
    strategy: String(openapi?.strategy ?? tradeSync.strategy ?? "broker_sync"),
    oauthAuthorizePath,
    oauthCallbackPath,
    syncEndpoint,
    oauthScope: String(openapi?.oauth_scope ?? "accounts"),
    accountIdField: String(openapi?.account_id_field ?? "ctid_trader_account_id"),
    summary,
    notes: String(openapi?.notes ?? tradeSync.notes ?? ""),
    fullHistoryRecommendation,
    historyBeforeSyncRequiresCsvImport: fullHistoryRecommendation === "csv_import",
    csvImportEndpoint: csvEndpoint,
  };
}

export function loadCtraderHistoryPath(): CtraderHistoryPath | null {
  if (typeof window === "undefined") return null;
  const v = window.sessionStorage.getItem(HISTORY_PATH_STORAGE_KEY);
  if (v === "recent_only" || v === "needs_csv_for_history") return v;
  return null;
}

export function saveCtraderHistoryPath(path: CtraderHistoryPath): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(HISTORY_PATH_STORAGE_KEY, path);
}

export function clearCtraderHistoryPath(): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(HISTORY_PATH_STORAGE_KEY);
}

export function stashCtraderOAuthAssetClasses(assetClasses: string[]): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(
    CTRADER_OAUTH_PENDING_ASSET_CLASSES_KEY,
    JSON.stringify(assetClasses.map((c) => c.trim().toLowerCase()).filter(Boolean))
  );
}

export function stashCtraderOAuthEnvironment(environment: string): void {
  if (typeof window === "undefined") return;
  const value = String(environment || "").trim().toLowerCase();
  if (value === "demo" || value === "live") {
    sessionStorage.setItem(CTRADER_OAUTH_PENDING_ENVIRONMENT_KEY, value);
  }
}

export function readCtraderOAuthEnvironment(): "demo" | "live" | null {
  if (typeof window === "undefined") return null;
  const raw = String(sessionStorage.getItem(CTRADER_OAUTH_PENDING_ENVIRONMENT_KEY) || "")
    .trim()
    .toLowerCase();
  if (raw === "demo" || raw === "live") return raw;
  return null;
}

export function stashCtraderOAuthMdFetchConsent(granted: boolean): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(CTRADER_OAUTH_PENDING_MD_FETCH_CONSENT_KEY, granted ? "1" : "0");
}

export function readCtraderOAuthMdFetchConsent(): boolean {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(CTRADER_OAUTH_PENDING_MD_FETCH_CONSENT_KEY) === "1";
}

export function readCtraderOAuthAssetClasses(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(CTRADER_OAUTH_PENDING_ASSET_CLASSES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map((v) => String(v).trim().toLowerCase()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

export function clearCtraderOAuthPending(): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(CTRADER_OAUTH_PENDING_ASSET_CLASSES_KEY);
  sessionStorage.removeItem(CTRADER_OAUTH_PENDING_ENVIRONMENT_KEY);
  sessionStorage.removeItem(CTRADER_OAUTH_PENDING_MD_FETCH_CONSENT_KEY);
}
